import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { addNewUser, getAllowedRegion } from "../service/UserService";
import { USER_ROLES } from "../constants/userRoles";
import { 
    Button, Form, Card, Container, Row, Col, 
    Alert, Spinner, InputGroup, Modal
} from "react-bootstrap";
import { 
    FaUser, FaLock, FaIdCard, FaUserTag, 
    FaPhone, FaEnvelope, FaUserCog, FaMapMarkerAlt, 
    FaEye, FaEyeSlash, FaArrowLeft, FaSave
} from "react-icons/fa";

export default function AddUser() {
    const [allowedRegions, setAllowedRegions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [alertVariant, setAlertVariant] = useState("info");
    let navigate = useNavigate();

    const [formDetail, setFormDetail] = useState({
        "username": "", "cos_customer_name": "", "agent_name": "", "role": "AGENT", "phone": "", "email": ""
    });
    const [responseMessage, setResponseMessage] = useState();

    useEffect(() => {
        setIsLoading(true);
        
        getAllowedRegion().then(response => {
            if (response.status === 200) {
                response.json().then(result => {
                    setAllowedRegions(result);
                    setFormDetail(currentFormDetail => ({
                        ...currentFormDetail, "region": result[0]
                    }));
                });
            } else {
                setResponseMessage("Error fetching allowed regions");
                setAlertVariant("danger");
                setFormDetail(null);
            }
        })
        .catch(error => {
            console.log('error', error);
            setResponseMessage("An error occurred while fetching data");
            setAlertVariant("danger");
        })
        .finally(() => {
            setIsLoading(false);
        });
    }, []);

    function handleFormChange(event) {
        setFormDetail({
            ...formDetail, [event.target.name]: event.target.value,
            blocked: false //For new user, hard code blocked to false
        });
    }

    function handleFormSubmit(event) {
        event.preventDefault();
        setShowConfirmModal(false);
        setResponseMessage("");
        setIsLoading(true);
        
        addNewUser(formDetail).then(response => {
            if (response.status === 201) {
                setResponseMessage("User Created Successfully");
                setAlertVariant("success");
                // Clear form after successful creation
                setFormDetail({
                    "username": "", "cos_customer_name": "", "agent_name": "", 
                    "role": "AGENT", "phone": "", "email": "", "region": formDetail.region
                });
            } else if (response.status === 400) {
                response.json().then(resp => {
                    setResponseMessage("Creation Failed: " + resp.message);
                    setAlertVariant("danger");
                });
            } else {
                response.text().then(result => {
                    setResponseMessage(result);
                    setAlertVariant("danger");
                });
            }
        })
        .catch(error => {
            console.log('error', error);
            setResponseMessage("An error occurred while creating user");
            setAlertVariant("danger");
        })
        .finally(() => {
            setIsLoading(false);
        });
    }
    
    function handleConfirmSubmit() {
        setShowConfirmModal(true);
    }
    
    function goBack() {
        navigate('/manage-user');
    }

    return (
        <Container className="py-4">
            <Button 
                variant="outline-secondary" 
                className="mb-3" 
                onClick={goBack}
            >
                <FaArrowLeft className="me-2" /> Back to Users
            </Button>
            
            <Card className="shadow-sm">
                <Card.Header as="h5" className="bg-primary text-white">
                    <FaUser className="me-2" /> Add New User
                </Card.Header>
                <Card.Body>
                    {responseMessage && (
                        <Alert variant={alertVariant} className="mb-4">
                            {responseMessage}
                        </Alert>
                    )}
                    
                    {isLoading && !allowedRegions.length ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Loading...</p>
                        </div>
                    ) : formDetail ? (
                        <Form onSubmit={handleFormSubmit}>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="username">
                                        <Form.Label>Username</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <FaUser />
                                            </InputGroup.Text>
                                            <Form.Control 
                                                name="username" 
                                                type="text" 
                                                placeholder="Username" 
                                                value={formDetail.username}
                                                required
                                                onChange={handleFormChange}
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="password">
                                        <Form.Label>Password</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <FaLock />
                                            </InputGroup.Text>
                                            <Form.Control 
                                                name="password" 
                                                type={showPassword ? "text" : "password"} 
                                                placeholder="Password"
                                                value={formDetail.password || ''}
                                                required
                                                onChange={handleFormChange}
                                            />
                                            <Button 
                                                variant="outline-secondary"
                                                onClick={() => setShowPassword(!showPassword)}
                                            >
                                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                                            </Button>
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                            </Row>
                            
                            <Row className="mt-2">
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="cos_customer_name">
                                        <Form.Label>Name</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <FaIdCard />
                                            </InputGroup.Text>
                                            <Form.Control 
                                                name="cos_customer_name" 
                                                type="text" 
                                                placeholder="Customer name"
                                                value={formDetail.cos_customer_name}
                                                onChange={handleFormChange}
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="agent_name">
                                        <Form.Label>Agent Name</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <FaUserTag />
                                            </InputGroup.Text>
                                            <Form.Control 
                                                name="agent_name" 
                                                type="text" 
                                                placeholder="Agent name"
                                                value={formDetail.agent_name}
                                                required
                                                onChange={handleFormChange}
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                            </Row>
                            
                            <Row className="mt-2">
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="phone">
                                        <Form.Label>Phone</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <FaPhone />
                                            </InputGroup.Text>
                                            <Form.Control 
                                                name="phone" 
                                                type="text" 
                                                placeholder="Phone number"
                                                value={formDetail.phone || ''}
                                                required
                                                onChange={handleFormChange}
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="email">
                                        <Form.Label>Email</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <FaEnvelope />
                                            </InputGroup.Text>
                                            <Form.Control 
                                                name="email" 
                                                type="email" 
                                                placeholder="Email address"
                                                value={formDetail.email || ''}
                                                required
                                                onChange={handleFormChange}
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                            </Row>
                            
                            <Row className="mt-2">
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="role">
                                        <Form.Label>Role</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <FaUserCog />
                                            </InputGroup.Text>
                                            <Form.Select 
                                                name="role" 
                                                value={formDetail.role}
                                                onChange={handleFormChange}
                                            >
                                                {USER_ROLES.map(role => (
                                                    <option key={role}>{role}</option>
                                                ))}
                                            </Form.Select>
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="region">
                                        <Form.Label>Region</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <FaMapMarkerAlt />
                                            </InputGroup.Text>
                                            <Form.Select 
                                                name="region" 
                                                value={formDetail.region}
                                                onChange={handleFormChange}
                                            >
                                                {allowedRegions.map((region, index) => (
                                                    <option key={index}>{region}</option>
                                                ))}
                                            </Form.Select>
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <div className="d-grid gap-2 d-md-flex justify-content-md-end mt-4">
                                <Button 
                                    variant="secondary" 
                                    className="me-md-2" 
                                    onClick={goBack}
                                >
                                    Cancel
                                </Button>
                                <Button 
                                    variant="primary" 
                                    onClick={handleConfirmSubmit}
                                    disabled={isLoading}
                                >
                                    {isLoading ? (
                                        <>
                                            <Spinner
                                                as="span"
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                                className="me-2"
                                            />
                                            Creating...
                                        </>
                                    ) : (
                                        <>
                                            <FaSave className="me-2" /> Create User
                                        </>
                                    )}
                                </Button>
                            </div>
                        </Form>
                    ) : (
                        <Alert variant="danger">
                            Unable to load form. Please try again later.
                        </Alert>
                    )}
                </Card.Body>
            </Card>
            
            {/* Confirmation Modal */}
            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm User Creation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to create a new user with the following details?
                    <ul className="mt-3">
                        <li><strong>Username:</strong> {formDetail.username}</li>
                        <li><strong>Name:</strong> {formDetail.cos_customer_name}</li>
                        <li><strong>Agent Name:</strong> {formDetail.agent_name}</li>
                        <li><strong>Role:</strong> {formDetail.role}</li>
                        <li><strong>Region:</strong> {formDetail.region}</li>
                    </ul>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleFormSubmit}>
                        Confirm
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
}
